// Thin wrapper over /api/consumer/injection/*. BE is the single source
// of truth — no localStorage caching. Cross-device sync is automatic
// because every page mount + visibilitychange re-pulls from the API.
//
// If a BE endpoint isn't shipped yet, callers get a clean empty state
// or a brand-styled error toast (handled at the call site). We do NOT
// silently mirror to localStorage — that fragments data per-device and
// gave members a misleading sense that everything was synced.
import { apiGet, apiPost } from "@/lib/api";

// === Foundations (the long-form guide) ===
//   GET /consumer/injection/foundations →
//     { title, body_md, version, lastUpdatedAt }
export const getFoundations = () => apiGet("/consumer/injection/foundations");

// === Protocol Card (member-specific) ===
//   GET /consumer/injection/protocol →
//     { compound, brandName?, dose, doseUnit, frequency, nextDueAt?,
//       reconstitution: { bacWaterMl, finalConcentrationMgMl, drawVolumeMl, syringeUnits },
//       schedule: { dayOfWeek?, timeOfDay?, intervalDays? },
//       vial: { reconstitutedAt?, expiresAt?, totalDoses?, dosesUsed?, dosesRemaining?, status },
//       careTeamPhone? }
export const getProtocol = () => apiGet("/consumer/injection/protocol");

// === Onboarding state ===
//   GET /consumer/injection/onboarding →
//     { completedAt?, currentStep, stepsCompleted: [], foundationsRead: bool,
//       acknowledgedAt? }
//   POST /consumer/injection/onboarding/step {step, completed: true}
//   POST /consumer/injection/onboarding/complete
export const getOnboarding = () => apiGet("/consumer/injection/onboarding");
export const markOnboardingStep = (step) =>
  apiPost("/consumer/injection/onboarding/step", { step, completed: true });
export const completeOnboarding = () =>
  apiPost("/consumer/injection/onboarding/complete", {});

// === Injection log ===
//   POST /consumer/injection/log
//     { compound, doseMg, site, loggedAt, notes?, difficulty? } → saved entry
//   GET /consumer/injection/history?limit=&before=
//     → { entries: [...], nextCursor? }
//
// Callers are expected to handle errors. We deliberately don't catch
// 404 / 5xx here — surface failures so the UI can render an empty
// state or error toast instead of pretending a save succeeded.
export const logInjection = (payload) =>
  apiPost("/consumer/injection/log", {
    compound: payload.compound,
    doseMg: payload.doseMg ?? null,
    site: payload.site,
    loggedAt: payload.loggedAt || new Date().toISOString(),
    notes: payload.notes || undefined,
    difficulty: payload.difficulty ?? undefined,
  });

export async function getHistory({ limit = 50 } = {}) {
  const remote = await apiGet(`/consumer/injection/history?limit=${limit}`);
  const entries = Array.isArray(remote?.entries)
    ? remote.entries
    : Array.isArray(remote)
    ? remote
    : [];
  return { entries: entries.slice(0, limit) };
}

// === Vial state — BE-authoritative ===
//   POST /consumer/injection/vial/start { totalDoses? } → updated vial
//   POST /consumer/injection/vial/increment             → updated vial
// Both endpoints return the same shape as protocol.vial.
export const startVial = ({ totalDoses } = {}) =>
  apiPost("/consumer/injection/vial/start", totalDoses ? { totalDoses } : {});
export const incrementVialDose = () =>
  apiPost("/consumer/injection/vial/increment", {});

// Pure helpers — exported for testing + reuse across the dashboard.
export function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.floor(ms / 86400000);
}
export function vialStatusFor(vial) {
  if (!vial?.expiresAt) return "unknown";
  const d = daysUntil(vial.expiresAt);
  if (d < 0) return "expired";
  if (d <= 7) return "expiring-soon";
  return "fresh";
}

// Best-effort estimator for total doses per vial when starting a new
// one without an explicit count. Caller can pass this into `startVial`
// so the BE has a reasonable default. Pure — no storage side-effects.
export function estimateTotalDoses(protocol) {
  if (!protocol) return 8;
  const recon = protocol.reconstitution || {};
  const dose = parseFloat(protocol.dose) || 0;
  const conc = parseFloat(recon.finalConcentrationMgMl) || 0;
  const water = parseFloat(recon.bacWaterMl) || 0;
  if (conc > 0 && water > 0 && dose > 0) {
    const totalMg = conc * water;
    return Math.max(1, Math.floor(totalMg / dose));
  }
  if (recon.drawVolumeMl && water > 0) {
    return Math.max(1, Math.floor(water / parseFloat(recon.drawVolumeMl)));
  }
  return 8;
}
